<template>
    <div class="autocomplete-field" @keyup="onKeyup">

        <el-input
            prefix-icon="el-icon-search"
            v-model="query"
            v-bind-events
            v-bind="$attrs"
            :label="label"
            :errors="errors"
            :value="value"
            :custom="custom"
            autocomplete="off"
            @blur="onBlur"
            @focus="onFocus"
            @input="$emit('input', query)">
        </el-input>

        <div class="autocomplete-list-wrapper">
            <ul class="autocomplete-list">
                <li v-for="(item, i) in predictions" :key="item.id" :item="item">
                    <a class="fin-px-5 fin-py-2 fin-flex fin-items-center fin-justify-start fin-leading-[20px]"
                       @click="onItemClick(item)">
                        <div><i class="fas fa-map-pin fin-mr-3"></i></div>
                        <div> {{ item.description }}</div>
                    </a>
                </li>
            </ul>
        </div>
    </div>
</template>

<script>
import geocode from 'vue-place-autocomplete/src/Helpers/Geocode';
import script from 'vue-interface/src/Helpers/Script';
import PlaceAutocompleteList from 'vue-place-autocomplete/src/PlaceAutocompleteList.vue';
import FormControl from 'vue-interface/src/Mixins/FormControl';
import FormGroup from 'vue-interface/src/Components/FormGroup';
import InputField from 'vue-interface/src/Components/InputField';
import ActivityIndicator from 'vue-interface/src/Components/ActivityIndicator';
import PlaceAutocompleteListItem from './PlaceAutoCompleteListItem';

const KEYCODE = {
    ESC: 27,
    LEFT: 37,
    UP: 38,
    RIGHT: 39,
    DOWN: 40,
    ENTER: 13,
    SPACE: 32,
    TAB: 9
};

const API_REQUEST_OPTIONS = [
    'bounds',
    'location',
    'component-restrictions',
    // 'offset',
    'radius',
    'types'
];

export default {
    name: 'PlaceAutoComplete',
    mixins: [
        FormControl
    ],
    components: {
        FormGroup,
        InputField,
        ActivityIndicator,
        PlaceAutocompleteList,
        PlaceAutocompleteListItem
    },

    watch: {
        value(value) {
            this.query = value;
        }
    },
    props: {
        apiKey: String,
        baseUri: {
            type: String,
            default: 'https://maps.googleapis.com/maps/api/js'
        },
        componentRestrictions: {
            type: [Boolean, Object, String],
            default: false
        },
        custom: Boolean,
        libraries: {
            type: Array,
            default() {
                return ['geometry', 'places'];
            }
        },
        bounds: {
            type: [Boolean, Object, String],
            default: false
        },
        location: {
            type: [Boolean, Object, String],
            default: false
        },
        offset: {
            type: Boolean,
            default: false
        },
        radius: {
            type: Boolean,
            default: false
        },
        types: {
            type: [Boolean, Array],
            default: false
        }
    },
    methods: {
        getInputElement() {
            return this.$el.querySelector('input');
        },
        getRequestOptions() {
            const inputElement = this.getInputElement();

            const options = {
                input: inputElement && inputElement.value ? String(inputElement.value) : ""
            };
            API_REQUEST_OPTIONS.forEach(prop => {
                if (this[prop] !== undefined || this[prop] !== null) {
                    options[prop] = this[prop];
                }
            });
            return options;
        },
        select(place) {
            // console.warn('autocomplete-select SELECT()')
            geocode({placeId: place.place_id}).then(response => {
                console.log('search ', response)
                this.$emit('input', this.query = response[0].formatted_address);
                // console.warn('autocomplete-select THEN()')
                this.$emit('autocomplete-select', place, response[0]);
            }).catch(response => {
                if (response == 'ZERO_RESULTS') {
                    // place_id not found - likely subpremise - geocode on address instead
                    geocode({address: place.description}).then(response => {
                        this.$emit('input', this.query = response[0].formatted_address);
                        // console.warn('autocomplete-select CATCH()')
                        this.$emit('autocomplete-select', place, response[0]);
                    })
                }
            });
        },
        search() {
            return new Promise((resolve, reject) => {
                if (!this.getInputElement().value) {
                    this.predictions = false;
                    this.showPredictions = false;
                    // reject(new Error('Input empty'));
                } else {
                    this.showActivityIndicator = true;

                    this.$service.getPlacePredictions(this.getRequestOptions(), (response, status) => {
                        this.showActivityIndicator = false;

                        switch (status) {
                            case window.google.maps.places.PlacesServiceStatus.OK:
                                this.recentPlaceSuggestions = response
                                this.$emit('search', response)
                                resolve(response);
                                break;
                            default:
                                reject(new Error(`Error with status: ${status}`));
                        }
                    });
                }
            });
        },

        hide() {
            this.showPredictions = false;
        },

        show() {
            this.showPredictions = true;
        },

        up() {
            const focused = this.$el.querySelector('a:focus');

            if (focused && focused.parentElement.previousElementSibling) {
                focused.parentElement.previousElementSibling.querySelector('a').focus();
            } else {
                const links = this.$el.querySelectorAll('a');
                links[links.length - 1].focus();
            }
        },

        down() {
            const focused = this.$el.querySelector('a:focus');

            if (focused && focused.parentElement.nextElementSibling) {
                focused.parentElement.nextElementSibling.querySelector('a').focus();
            } else {
                this.$el.querySelector('a').focus();
            }
        },

        onKeydown(event) {
            const element = this.$el.querySelector('[tabindex]');

            if (element && event.keyCode === KEYCODE.TAB) {
                event.preventDefault() && element.focus();
            }
        },

        onKeyup(event) {
            switch (event.keyCode) {
                case KEYCODE.ENTER:
                case KEYCODE.SPACE:
                    if (this.$el.querySelector('.is-focused')) {
                        this.$el.querySelector('.is-focused a').dispatchEvent(new Event('mousedown'));
                    }
                    return;
                case KEYCODE.ESC:
                    this.hide();
                    this.getInputElement().blur();
                    return;
                case KEYCODE.UP:
                    this.up();
                    event.preventDefault();
                    return;
                case KEYCODE.DOWN:
                    this.down();
                    event.preventDefault();
                    return;
            }

            this.search().then(response => {
                this.predictions = response;
                this.showPredictions = true;
            }, error => {
                if (error) {
                    this.predictions = false;
                }
            });
        },

        onFocus(event) {
            if (this.query) {
                if (!this.predictions.length) {
                    this.onKeyup(event);
                }
                this.show();
            }
            this.$emit('focus')
            // console.warn('focus input')
        },

        onBlur(event) {
            if (!this.$el.contains(event.relatedTarget)) {
                this.hide();
            }
            this.$emit('blur')

            // console.warn('blur input')

            setTimeout(() => {
                // console.warn('delayed blur setTimeout')
                const firstPrediction = this.predictions[0]
                if (firstPrediction) this.select(firstPrediction)
                this.predictions = false
            }, 500)
        },

        onItemBlur(event) {
            this.onBlur(event);
        },

        onItemClick(item) {
            this.select(item)
            this.lastClickedItem = item
            this.predictions = [item]
            this.$emit('itemClick')
        }

    },

    mounted() {
        if (this.apiKey) {
            script(`${this.baseUri}?key=${this.apiKey}&callback=anonymous&libraries=${this.libraries.join(',')}`).then(() => {
                this.$geocoder = new window.google.maps.Geocoder();
                this.$service = new window.google.maps.places.AutocompleteService();
                this.loaded = true;
                this.$emit('loaded');
            });
        }
    },

    data() {
        return {
            loaded: false,
            predictions: false,
            query: this.value,
            showPredictions: false,
            showActivityIndicator: this.activity,
            recentPlaceSuggestions: null,
            lastClickedItem: null
        };
    }
};
</script>

<style lang="scss">
.autocomplete-field {
    position: relative;

    .activity-indicator {
        right: .5rem;
        bottom: .5rem;
        position: absolute;
    }
}

.autocomplete-list-wrapper {
    position: absolute;
    z-index: 10;
    width: 100%;
    top: 100%;
    background: white;
}

.autocomplete-list {
    margin: 0;
    padding: 0;
    box-shadow: 0 0 10px rgba(0, 0, 0, .25);
}
</style>
